// frontend/packages/business-console/src/components/layout/Layout.jsx
//
// Application shell. Mirrors the UpRyzze Tools pattern (apps/tools-web/
// src/ui/AppShell.tsx) so the two surfaces feel like one product:
//   - Desktop:  fixed left sidebar (248px) + main column with a sticky
//               top bar that hosts theme toggle + logout.
//   - Mobile:   thin AppBar with a hamburger that opens the sidebar in a
//               temporary Drawer. Logout sits inside the drawer footer.
//
// Most pages render inside a centered, padded main column. The Content
// Editor opts out via `isFullBleed` because it manages its own scroll
// panes — the sidebar shell still wraps it, but the main pane fills.

import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { Link as RouterLink, useLocation } from "react-router-dom";

import Sidebar, { SIDEBAR_WIDTH } from "./Sidebar";
import MobileTopBar from "./MobileTopBar";
import MobileBottomNav from "./MobileBottomNav";
import ErrorBoundary from "../common/ErrorBoundary";
import StaffModeBanner, { BANNER_HEIGHT as STAFF_BANNER_HEIGHT } from "../staff/StaffModeBanner";
import ClientSwitcher from "../staff/ClientSwitcher";
import AiUsageMeter from "../AiUsageMeter";
import NotificationBell from "../NotificationBell";
import { SnackbarProvider } from "../../contexts/SnackbarContext";
import { MediaConfigProvider } from "../../contexts/MediaConfigContext";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeMode } from "../../theme";

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const { isImpersonating } = useAuth();

  // The content editor manages its own scroll panes; opt out of the
  // standard centered/padded main column so it can fill the viewport.
  const isFullBleed =
    location.pathname.endsWith("/content") ||
    location.pathname.endsWith("/build");

  // Staff banner is a fixed-position overlay at the very top of the
  // viewport. Mirror its height into every fixed/sticky chrome element
  // (sidebar, mobile AppBar, desktop sticky topbar) so nothing clips
  // under it. Computed once here and threaded down via props/sx.
  // Only when actually impersonating a client (not on your own business).
  const showStaffBanner = isImpersonating;
  const bannerOffset = showStaffBanner ? STAFF_BANNER_HEIGHT : 0;

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <SnackbarProvider>
      <MediaConfigProvider>
        <StaffModeBanner />
        <Box
          sx={{
            display: "flex",
            minHeight: "100vh",
            bgcolor: "background.default",
            color: "text.primary",
          }}
        >
          {isMobile && (
            <MobileTopBar
              onOpenNav={() => setMobileNavOpen(true)}
              topOffset={bannerOffset}
            />
          )}

          {/* Sidebar */}
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileNavOpen}
              onClose={closeMobileNav}
              ModalProps={{ keepMounted: true }}
              PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: 0 } }}
            >
              <Sidebar onNavigate={closeMobileNav} />
            </Drawer>
          ) : (
            <Box
              component="nav"
              sx={{ width: SIDEBAR_WIDTH, flexShrink: 0 }}
              aria-label="Primary navigation"
            >
              <Box
                sx={{
                  position: "fixed",
                  top: bannerOffset,
                  left: 0,
                  bottom: 0,
                  width: SIDEBAR_WIDTH,
                }}
              >
                <Sidebar />
              </Box>
            </Box>
          )}

          {/* Main column */}
          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              ...(isMobile
                ? {
                    pt: `${56 + bannerOffset}px`,
                    // Clear the fixed bottom tab bar (+ home-indicator inset).
                    pb: `calc(${theme.custom.mobile.bottomNavHeight}px + env(safe-area-inset-bottom))`,
                  }
                : {}),
            }}
          >
            {!isMobile && (
              <Box
                sx={{
                  position: "sticky",
                  top: bannerOffset,
                  zIndex: theme.zIndex.appBar,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.5,
                  px: 4,
                  minHeight: 56,
                  bgcolor: "background.paper",
                  color: "text.primary",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ mr: "auto" }}>
                  <ClientSwitcher />
                </Box>
                <Box sx={{ mr: 1 }}>
                  <AiUsageMeter variant="compact" />
                </Box>
                <Box sx={{ mr: 0.5 }}>
                  <NotificationBell />
                </Box>
                <Tooltip
                  title={
                    mode === "dark" ? "Switch to light mode" : "Switch to dark mode"
                  }
                >
                  <IconButton
                    onClick={toggleMode}
                    size="small"
                    aria-label="Toggle theme"
                    sx={{ color: "text.secondary" }}
                  >
                    {mode === "dark" ? (
                      <LightModeOutlinedIcon fontSize="small" />
                    ) : (
                      <DarkModeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Log out">
                  <IconButton
                    component={RouterLink}
                    to="/logout"
                    size="small"
                    aria-label="Log out"
                    sx={{ color: "text.secondary" }}
                  >
                    <LogoutOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Box
              sx={
                isFullBleed
                  ? {
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }
                  : {
                      flex: 1,
                      px: { xs: 2, md: 4 },
                      py: { xs: 3, md: 4 },
                      width: "100%",
                      maxWidth: 1200,
                      mx: "auto",
                    }
              }
            >
              <ErrorBoundary>{children}</ErrorBoundary>
            </Box>
          </Box>

          {isMobile && <MobileBottomNav />}
        </Box>
      </MediaConfigProvider>
    </SnackbarProvider>
  );
};

export default React.memo(Layout);
